import moment, { Moment } from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import {
  PackagesFormValues,
  PackagesGuest,
} from '@features/DynamicPackagesConfigurationWizard/types';
import { ExperiencesByDay, DateGroupData } from '../types';
import { FlattenedExperience, flattenExperiences } from './flattenExperiences';

export interface UseDateGroupSelectionProps {
  selectedExperiencesByDay: ExperiencesByDay[];
  journeyParams: {
    fromDate: string | Moment;
  };
  guests: PackagesGuest[];
  setValue: UseFormSetValue<PackagesFormValues>;
  getValues: UseFormGetValues<PackagesFormValues>;
  spaAvailableDates?: string[];
  isRepeatedFlexibleEnabled?: boolean;
}

interface UseDateGroupSelectionReturn {
  dateGroupData: DateGroupData[];
  handleDayChange: (groupKey: string, selectedDateString: string) => void;
  handleGuestDayChange: (groupKey: string, guestIndex: number, selectedDateString: string) => void;
  clearGuestOverridesForGroup: (groupKey: string) => void;
  getGuestDay: (groupKey: string, guestIndex: number) => number | undefined;
  getGuestAvailableDates: (groupKey: string, guestIndex: number) => string[];
}

const effectiveGroupKey = (group: ExperiencesByDay): string => group.groupKey ?? group.date;

const filterDateRange = (
  dateRange: string[],
  fromDate: Moment,
  blockedDays: Set<number>,
  spaAvailableDates: string[]
): string[] =>
  dateRange
    .filter((dateStr) => {
      const parsed = moment(dateStr);
      const dayNum = parsed.diff(fromDate, 'days');
      if (blockedDays.has(dayNum)) return false;
      const formatted = parsed.format('YYYY-MM-DD');
      return spaAvailableDates.length === 0 || spaAvailableDates.includes(formatted);
    })
    .map((dateStr) => moment(dateStr).format('YYYY-MM-DD'));

const computeBlockedDays = (
  selectedExperiencesByDay: ExperiencesByDay[],
  targetGroupKey: string,
  targetOptionGroupId: number | string | undefined,
  selectedDayByGroup: Record<string, number>,
  selectedDayByGroupGuest: Record<string, Record<number, number>>,
  guestIndex: number | null
): Set<number> => {
  const blocked = new Set<number>();
  const targetIndex = selectedExperiencesByDay.findIndex(
    (g) => effectiveGroupKey(g) === targetGroupKey
  );
  const targetHasGroupKey = selectedExperiencesByDay[targetIndex]?.groupKey !== undefined;

  selectedExperiencesByDay.forEach((other, otherIndex) => {
    const otherKey = effectiveGroupKey(other);
    if (otherKey === targetGroupKey) return;
    // Flag-OFF (no groupKey): preserve today's order-based blocking — only earlier
    // groups in the array block the current one. Flag-ON (groupKey present):
    // symmetric blocking across all repetitions of the same optionGroupId.
    if (!targetHasGroupKey && otherIndex >= targetIndex) return;
    if (other.groupKey !== undefined) {
      const otherOptionGroupId = other.options[0]?.optionGroupId;
      if (otherOptionGroupId !== targetOptionGroupId) return;
    }
    if (guestIndex !== null) {
      // Per-guest mode: this guest's day on the other group blocks here.
      const day = selectedDayByGroupGuest[otherKey]?.[guestIndex] ?? selectedDayByGroup[otherKey];
      if (day !== undefined) blocked.add(day);
    } else {
      // Shared-picker mode: applying a shared day writes to every guest, so
      // any day already taken by ANY guest's per-guest override on another
      // repetition of the same optionGroupId must be blocked — otherwise we
      // would overwrite that guest's slot with a colliding day.
      const overrides = selectedDayByGroupGuest[otherKey];
      if (overrides) {
        Object.values(overrides).forEach((d) => {
          if (d !== undefined) blocked.add(d);
        });
      }
      const sharedDay = selectedDayByGroup[otherKey];
      if (sharedDay !== undefined) blocked.add(sharedDay);
    }
  });

  return blocked;
};

export const useDateGroupSelection = ({
  selectedExperiencesByDay,
  journeyParams,
  guests,
  setValue,
  getValues,
  spaAvailableDates = [],
  isRepeatedFlexibleEnabled = false,
}: UseDateGroupSelectionProps): UseDateGroupSelectionReturn => {
  const [selectedDayByGroup, setSelectedDayByGroup] = useState<Record<string, number>>({});
  const [selectedDayByGroupGuest, setSelectedDayByGroupGuest] = useState<
    Record<string, Record<number, number>>
  >({});
  const fromDate = useMemo(() => moment(journeyParams.fromDate), [journeyParams.fromDate]);

  const flattenedExperiences = useMemo(
    () => flattenExperiences(selectedExperiencesByDay),
    [selectedExperiencesByDay]
  );

  const experiencesByGroupKey = useMemo(() => {
    const map = new Map<string, FlattenedExperience[]>();
    selectedExperiencesByDay.forEach((group) => {
      const key = effectiveGroupKey(group);
      const items = flattenedExperiences.filter((exp) =>
        group.groupKey ? exp.groupKey === group.groupKey : exp.date === group.date
      );
      map.set(key, items);
    });
    return map;
  }, [selectedExperiencesByDay, flattenedExperiences]);

  const dateGroupData = useMemo<DateGroupData[]>(() => {
    return selectedExperiencesByDay.map((dayGroup) => {
      const { date, dateRange } = dayGroup;
      const groupKey = effectiveGroupKey(dayGroup);
      const selectedDay = selectedDayByGroup[groupKey];
      const currentOptionGroupId = dayGroup.options[0]?.optionGroupId;

      const blockedDays = computeBlockedDays(
        selectedExperiencesByDay,
        groupKey,
        currentOptionGroupId,
        selectedDayByGroup,
        selectedDayByGroupGuest,
        null
      );

      const availableDates = filterDateRange(dateRange, fromDate, blockedDays, spaAvailableDates);

      let autoSelectedDay: number | undefined;
      if (availableDates.length === 1 && selectedDay === undefined) {
        autoSelectedDay = moment(availableDates[0]).diff(fromDate, 'days');
      }

      return {
        date,
        dateRange,
        groupKey,
        selectedDay: autoSelectedDay ?? selectedDay,
        availableDates,
        experiences: experiencesByGroupKey.get(groupKey) ?? [],
        isConflicting: selectedDay !== undefined && blockedDays.has(selectedDay),
      };
    });
  }, [
    selectedExperiencesByDay,
    experiencesByGroupKey,
    selectedDayByGroup,
    selectedDayByGroupGuest,
    fromDate,
    spaAvailableDates,
  ]);

  const guestAvailableDatesByGroup = useMemo(() => {
    // Per-guest available-date matrices are only consumed by the flag-ON UI.
    // Skip the work entirely when the flag is OFF to avoid the per-render cost
    // for every wizard render of legacy packages.
    if (!isRepeatedFlexibleEnabled) return new Map<string, Map<number, string[]>>();
    const result = new Map<string, Map<number, string[]>>();
    selectedExperiencesByDay.forEach((group) => {
      const groupKey = effectiveGroupKey(group);
      const optionGroupId = group.options[0]?.optionGroupId;
      const perGuest = new Map<number, string[]>();
      guests.forEach((_, guestIndex) => {
        const blockedDays = computeBlockedDays(
          selectedExperiencesByDay,
          groupKey,
          optionGroupId,
          selectedDayByGroup,
          selectedDayByGroupGuest,
          guestIndex
        );
        perGuest.set(
          guestIndex,
          filterDateRange(group.dateRange, fromDate, blockedDays, spaAvailableDates)
        );
      });
      result.set(groupKey, perGuest);
    });
    return result;
  }, [
    isRepeatedFlexibleEnabled,
    selectedExperiencesByDay,
    selectedDayByGroup,
    selectedDayByGroupGuest,
    guests,
    fromDate,
    spaAvailableDates,
  ]);

  useMemo(() => {
    const updates: Record<string, number | undefined> = {};
    let hasChanges = false;

    dateGroupData.forEach(({ groupKey, selectedDay, isConflicting }) => {
      const currentSelection = selectedDayByGroup[groupKey];

      if (isConflicting && currentSelection !== undefined) {
        updates[groupKey] = undefined;
        hasChanges = true;
      } else if (selectedDay !== undefined && currentSelection !== selectedDay) {
        updates[groupKey] = selectedDay;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setSelectedDayByGroup((prev) => {
        const next = { ...prev };
        Object.entries(updates).forEach(([key, value]) => {
          if (value === undefined) {
            delete next[key];
          } else {
            next[key] = value;
          }
        });
        return next;
      });
    }
  }, [dateGroupData, selectedDayByGroup]);

  const resetSlot = useCallback(
    (guestIndex: number, experienceIndex: number, dayNumber: number) => {
      const current = getValues(`guests.${guestIndex}.experiences.${experienceIndex}`) || {};
      setValue(
        `guests.${guestIndex}.experiences.${experienceIndex}`,
        {
          ...current,
          dayNumber,
          startTime: undefined,
          endTime: undefined,
          visitDate: undefined,
        } as any,
        { shouldValidate: false }
      );
    },
    [getValues, setValue]
  );

  const handleDayChange = useCallback(
    (groupKey: string, selectedDateString: string) => {
      const dayNum = moment(selectedDateString).diff(fromDate, 'days');
      setSelectedDayByGroup((prev) => ({ ...prev, [groupKey]: dayNum }));

      const experiences = experiencesByGroupKey.get(groupKey) ?? [];
      experiences.forEach(({ experienceIndex }) => {
        guests.forEach((_, guestIndex) => resetSlot(guestIndex, experienceIndex, dayNum));
      });
    },
    [experiencesByGroupKey, fromDate, guests, resetSlot]
  );

  const handleGuestDayChange = useCallback(
    (groupKey: string, guestIndex: number, selectedDateString: string) => {
      const allowed = guestAvailableDatesByGroup.get(groupKey)?.get(guestIndex) ?? [];
      const targetDate = moment(selectedDateString).format('YYYY-MM-DD');
      if (!allowed.includes(targetDate)) return;

      const dayNum = moment(selectedDateString).diff(fromDate, 'days');
      setSelectedDayByGroupGuest((prev) => ({
        ...prev,
        [groupKey]: { ...(prev[groupKey] || {}), [guestIndex]: dayNum },
      }));

      const experiences = experiencesByGroupKey.get(groupKey) ?? [];
      experiences.forEach(({ experienceIndex }) => resetSlot(guestIndex, experienceIndex, dayNum));
    },
    [experiencesByGroupKey, fromDate, guestAvailableDatesByGroup, resetSlot]
  );

  const clearGuestOverridesForGroup = useCallback(
    (groupKey: string) => {
      setSelectedDayByGroupGuest((prev) => {
        if (!prev[groupKey]) return prev;
        const next = { ...prev };
        delete next[groupKey];
        return next;
      });

      // Reset slots to whichever day the shared picker would land on. Prefer the
      // explicit shared selection; fall back to the auto-selected day from
      // dateGroupData (e.g. when only one date is available); if neither exists
      // there's nothing meaningful to write — leave slots untouched and let the
      // user pick fresh on the shared picker.
      const sharedDay =
        selectedDayByGroup[groupKey] ??
        dateGroupData.find((g) => g.groupKey === groupKey)?.selectedDay;
      if (sharedDay === undefined) return;

      const experiences = experiencesByGroupKey.get(groupKey) ?? [];
      experiences.forEach(({ experienceIndex }) => {
        guests.forEach((_, guestIndex) => resetSlot(guestIndex, experienceIndex, sharedDay));
      });
    },
    [dateGroupData, experiencesByGroupKey, guests, resetSlot, selectedDayByGroup]
  );

  const getGuestDay = useCallback(
    (groupKey: string, guestIndex: number): number | undefined => {
      const override = selectedDayByGroupGuest[groupKey]?.[guestIndex];
      if (override !== undefined) return override;
      return selectedDayByGroup[groupKey];
    },
    [selectedDayByGroup, selectedDayByGroupGuest]
  );

  const getGuestAvailableDates = useCallback(
    (groupKey: string, guestIndex: number): string[] =>
      guestAvailableDatesByGroup.get(groupKey)?.get(guestIndex) ?? [],
    [guestAvailableDatesByGroup]
  );

  return {
    dateGroupData,
    handleDayChange,
    handleGuestDayChange,
    clearGuestOverridesForGroup,
    getGuestDay,
    getGuestAvailableDates,
  };
};
